const colors = require('tailwindcss/colors');

/**
 * Util function to convert px values into rem.
 * Useful for getting better QA and collab from designers.
 * @param {*} px
 * @returns A rem value calculated from a 16px base font size.
 */
const rem = (px) => `${px / 16}rem`;

/** @type {import('tailwindcss/tailwind-config').TailwindConfig} */
module.exports = {
  content: [
    './pages/**/*.{js,ts,jsx,tsx}',
    './components/**/*.{js,ts,jsx,tsx}',
    './blocks/**/*.{js,ts,jsx,tsx}',
    './atoms/**/*.{js,ts,jsx,tsx}',
  ],
  theme: {
    colors: {
      'warm-gray': '#E5E5E1',
      'lighter-gray-8': 'rgba(196, 196, 196, 0.08)',
      /* gray color level based on Figma https://www.figma.com/file/v2wCTt25tDCX3spdVrlgb1/%5BWIP%5D-hydraulics.nyc-2022?node-id=701%3A23068 */
      'gray-01': '#9E9E9E',
      'gray-02': '#696862',
      'gray-03': '#454545',
      'gray-04': '#2C2C2B',
      'gray-05': '#DCDCDC',
      'dark-gray': '#747474',
      'white': '#FFFFFF',
      'white-tint': '#F5F5F4',
      'white-tint-opaque': '#F5F5F4B3',
      'white-skeleton': 'rgba(248, 248, 248, 0.1)',
      'black': '#181818',
      'black-skeleton': 'rgba(24, 24, 24, 0.1)',
      'black-tint': 'rgba(24, 24, 24, 0.6)',
      'error': '#ffd8d8',
      'sand': '#E2DFD2',
      'yellow': '#F2FF5A',
    },
    fontFamily: {
      'serif': [
        'ABC Synt',
        'Libre Bodoni',
        'Bodoni',
        'Noto Serif Display',
        'New York',
        'IBM Plex Serif',
        'Times New Roman',
        'Times',
        'serif',
      ],
      'whyte': [
        'Whyte',
        'ui-sans-serif',
        'system-ui',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'Noto Sans',
        'sans-serif',
        'Apple Color Emoji',
        'Segoe UI Emoji',
        'Segoe UI Symbol',
        'Noto Color Emoji',
      ],
      'whyte-inktrap': [
        'Whyte Inktrap',
        'ui-sans-serif',
        'system-ui',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'Noto Sans',
        'sans-serif',
        'Apple Color Emoji',
        'Segoe UI Emoji',
        'Segoe UI Symbol',
        'Noto Color Emoji',
      ],
    },
    fontWeight: {
      light: 300,
      book: 350,
      medium: 500,
    },
    fontSize: {
      // mobile
      'xl': [
        rem(32),
        {
          lineHeight: rem(38),
          letterSpacing: '-0.02rem',
        },
      ],
      'large': [
        rem(24),
        {
          lineHeight: rem(34),
          letterSpacing: '-0.02rem',
        },
      ],
      'med': [
        rem(20),
        {
          lineHeight: rem(28),
          letterSpacing: '-0.02rem',
        },
      ],
      'small': [
        rem(16),
        {
          lineHeight: rem(24),
          letterSpacing: '-0.02rem',
        },
      ],
      // desktop
      'desktop-xl': [
        rem(48),
        {
          lineHeight: rem(51.2),
          letterSpacing: '-0.02rem',
        },
      ],
      'desktop-large': [
        rem(32),
        {
          lineHeight: rem(38.4),
          letterSpacing: '-0.02rem',
        },
      ],
      'desktop-med': [
        rem(20),
        {
          lineHeight: rem(34),
          letterSpacing: '-0.02rem',
        },
      ],
      'desktop-small': [
        rem(16),
        {
          lineHeight: rem(24),
          letterSpacing: '-0.02rem',
        },
      ],
    },
    typography: (theme) => ({
      base: {
        css: [
          {
            '--tw-prose-body': colors.black,
            '--tw-prose-headings': colors.black,
            '--tw-prose-lead': colors.black,
            '--tw-prose-links': 'currentColor',
            '--tw-prose-bold': 'currentColor',
            '--tw-prose-counters': colors.gray,
            '--tw-prose-bullets': colors.gray,
            '--tw-prose-hr': colors.gray,
            '--tw-prose-quotes': colors.black,
            '--tw-prose-quote-borders': colors.gray,
            '--tw-prose-captions': colors.gray,
            '--tw-prose-invert-body': colors.white,
            '--tw-prose-invert-headings': colors.white,
            '--tw-prose-invert-lead': colors.white,
            '--tw-prose-invert-links': 'currentColor',
            '--tw-prose-invert-bold': 'currentColor',
            '--tw-prose-invert-counters': colors.gray,
            '--tw-prose-invert-bullets': colors.gray,
            '--tw-prose-invert-hr': colors.gray,
            '--tw-prose-invert-quotes': colors.white,
            '--tw-prose-invert-quote-borders': colors.gray,
            '--tw-prose-invert-captions': colors.gray,
          },
          {
            'p': {
              fontSize: theme('fontSize.desktop-med')[0],
              fontWeight: theme('fontWeight.book'),
              marginTop: theme('spacing.content-skinny'),
              marginBottom: theme('spacing.content-skinny'),
              lineHeight: 1.5,
            },
            'a': {
              textDecoration: 'underline',
              textUnderlineOffset: 4,
              textDecorationStyle: 'dashed',
              textDecorationThickness: 1,
              textDecorationColor: theme('colors.gray-03'),
              transition: 'all 0.2s ease-in-out',
            },
            'a:hover': {
              background: theme('colors.yellow'),
              borderRadius: '0.25rem',
            },
            'blockquote': {
              marginTop: theme('spacing.content-wide'),
              marginBottom: theme('spacing.content-wide'),
              padding: 32,
              fontWeight: theme('fontWeight.book'),
            },
            'h1': {
              fontSize: theme('fontSize.desktop-xl')[0],
              marginBottom: theme('spacing.content-skinny'),
              lineHeight: 1.2,
              letterSpacing: -1.5,
              fontWeight: theme('fontWeight.light'),
            },
            'h2': {
              fontSize: theme('fontSize.desktop-large')[0],
              marginTop: theme('spacing.content-wide'),
              marginBottom: theme('spacing.content-skinny'),
              lineHeight: 1.2,
              letterSpacing: -0.02,
              fontWeight: theme('fontWeight.book'),
            },
            'h3': {
              fontSize: theme('fontSize.desktop-med')[0],
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-narrow'),
              lineHeight: 1.2,
              fontWeight: theme('fontWeight.medium'),
            },
            'h4': {
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-narrow'),
              lineHeight: 1.2,
            },
            'img': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'video': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'figure': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'figure > *': {
              marginTop: 0,
              marginBottom: 0,
            },
            'figcaption': {
              fontSize: theme('fontSize.desktop-small')[0],
              lineHeight: theme('fontSize.desktop-small'),
              marginTop: theme('spacing.content-narrow'),
            },
            'ol, ul': {
              fontSize: theme('fontSize.desktop-med')[0],
              fontWeight: theme('fontWeight.book'),
              lineHeight: 1.4,
            },
            'ol': {
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-normal'),
              paddingLeft: theme('spacing.content-medium'),
            },
            'ul': {
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-normal'),
              paddingLeft: theme('spacing.content-narrow'),
            },
            'li': {
              marginTop: 8,
              marginBottom: 8,
            },
            'ol > li': {
              paddingLeft: 0,
            },
            'ol > li > span': {
              marginLeft: -12,
            },
            'ul > li': {
              paddingLeft: 0,
            },
            '> ul > li p': {
              marginTop: 12,
              marginBottom: 12,
            },
            '> ul > li > *:first-child': {
              marginTop: theme('spacing.content-narrow'),
            },
            '> ul > li > *:last-child': {
              marginBottom: theme('spacing.content-narrow'),
            },
            '> ol > li > *:first-child': {
              marginTop: theme('spacing.content-narrow'),
            },
            '> ol > li > *:last-child': {
              marginBottom: theme('spacing.content-narrow'),
            },
            'ul ul, ul ol, ol ul, ol ol': {
              marginTop: 12,
              marginBottom: 12,
            },
            'hr': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'hr + *': {
              marginTop: 0,
            },
            'h2 + *': {
              marginTop: 0,
            },
            'h3 + *': {
              marginTop: 0,
            },
            'h4 + *': {
              marginTop: 0,
            },
            'p + h1': {
              marginTop: theme('spacing.wide'),
            },
            'p + ul, p + ol': {
              marginTop: 0,
            },
          },
          {
            '> :first-child': {
              marginTop: 0,
            },
            '> :last-child': {
              marginBottom: 0,
            },
          },
        ],
      },
      small: {
        css: [
          {
            'p': {
              fontSize: theme('fontSize.small')[0],
              fontWeight: theme('fontWeight.book'),
              marginTop: theme('spacing.content-skinny'),
              marginBottom: theme('spacing.content-skinny'),
              lineHeight: 1.4,
            },
            'a': {
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              textDecorationThickness: 1,
            },
            'blockquote': {
              marginTop: theme('spacing.content-wide'),
              marginBottom: theme('spacing.content-wide'),
              padding: 16,
              fontWeight: theme('fontWeight.book'),
            },
            'h1': {
              fontSize: theme('fontSize.xl')[0],
              marginBottom: theme('spacing.content-skinny'),
              lineHeight: 1.2,
              letterSpacing: -0.02,
              fontWeight: theme('fontWeight.light'),
            },
            'h2': {
              fontSize: theme('fontSize.large')[0],
              marginTop: theme('spacing.content-wide'),
              marginBottom: theme('spacing.content-skinny'),
              lineHeight: 1.4,
              letterSpacing: -0.02,
              fontWeight: theme('fontWeight.book'),
            },
            'h3': {
              fontSize: theme('fontSize.med')[0],
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-narrow'),
              lineHeight: 1.2,
              fontWeight: theme('fontWeight.medium'),
            },
            'h4': {
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-narrow'),
              lineHeight: 1.2,
            },
            'img': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'video': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'figure': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'figure > *': {
              marginTop: 0,
              marginBottom: 0,
            },
            'figcaption': {
              fontSize: theme('fontSize.small')[0],
              lineHeight: theme('fontSize.small'),
              marginTop: theme('spacing.content-narrow'),
            },
            'ol, ul': {
              fontSize: theme('fontSize.small')[0],
              fontWeight: theme('fontWeight.book'),
              lineHeight: 1.4,
            },
            'ol': {
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-normal'),
              paddingLeft: theme('spacing.content-tiny'),
            },
            'ul': {
              marginTop: theme('spacing.content-normal'),
              marginBottom: theme('spacing.content-normal'),
              paddingLeft: 0,
            },
            'li': {
              marginTop: 8,
              marginBottom: 8,
            },
            'ol > li': {
              paddingLeft: 0,
            },
            'ol > li > span': {
              marginLeft: -8,
            },
            'ul > li': {
              paddingLeft: 0,
            },
            '> ul > li p': {
              marginTop: 12,
              marginBottom: 12,
            },
            '> ul > li > *:first-child': {
              marginTop: theme('spacing.content-narrow'),
            },
            '> ul > li > *:last-child': {
              marginBottom: theme('spacing.content-narrow'),
            },
            '> ol > li > *:first-child': {
              marginTop: theme('spacing.content-narrow'),
            },
            '> ol > li > *:last-child': {
              marginBottom: theme('spacing.content-narrow'),
            },
            'ul ul, ul ol, ol ul, ol ol': {
              marginTop: 12,
              marginBottom: 12,
            },
            'hr': {
              marginTop: theme('spacing.content-widest'),
              marginBottom: theme('spacing.content-widest'),
            },
            'hr + *': {
              marginTop: 0,
            },
            'h2 + *': {
              marginTop: 0,
            },
            'h3 + *': {
              marginTop: 0,
            },
            'h4 + *': {
              marginTop: 0,
            },
            'p + h1': {
              marginTop: theme('spacing.wide'),
            },
            'p + ul, p + ol': {
              marginTop: 0,
            },
          },
        ],
      },
    }),
    screens: {
      'hover-supported': { raw: '(hover: hover)' },
      'tablet': '640px',
      'laptop': '1024px',
      'desktop': '1280px',
      'larger-desktop': '1536px',
      'x-larger-desktop': '2160px',
    },
    backgroundImage: (theme) => ({
      'white-tint-gradient': `linear-gradient(180deg, ${theme(
        'colors.white-tint',
      )} 51.79%, rgba(245, 245, 244, 0) 100%)`,
    }),
    extend: {
      spacing: {
        'content-widest': rem(176),
        'content-wide': rem(64),
        'content-normal': rem(48),
        'content-medium': rem(36),
        'content-narrow': rem(24),
        'content-skinny': rem(16),
        'content-tiny': rem(8),
      },
      height: {
        'tab': '64px',
        'tab-hovered': '112px',
      },
      boxShadow: {
        tab: 'rgba(0,0,0,0.02) 0 -6px 16px',
      },
      zIndex: {
        nav: '500',
        loading: '600',
        snippetContentScrolled: '10',
        snippetContentHovered: '700',
      },
      translate: {
        'over-screen': '120%',
      },
    },
  },
  plugins: [
    require('@tailwindcss/typography')({
      className: 'RichText',
    }),
  ],
};
